import { useMemo } from 'react'

import { useWindowShell, useWindowStack } from '@krp/client-runtime'
import {
  CATALOG_WRITE,
  PLATFORM_WINDOWS,
  grantsOf,
  holdScanFrame,
  lastRetryPhoto,
  newUuid,
  platformSearchConfirm,
  platformSearchFeedback,
  scanEventRecorded,
  scanEventText,
  scanSessionVm,
  type ScanEventKind,
  type ScanSessionVm,
  type SearchFeedbackAction,
  type SearchHints,
  type VerdictActions,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useMyAccess } from './useMyAccess'
import { useOpenCommonProduct } from './useProductLinks'

/** Название в адресе окна не длиннее 120 знаков: адрес не место для длинных текстов. */
const NAME_LIMIT = 120

/**
 * Что делает человек с ответом поиска по фото (RP-FB56) и общая смена сканирования. Один код на «Выбрать фото» и на сканер.
 * Окна открываются поверх текущего (`?w=`): сканер остаётся в стопке, возврат один — «← назад». Кадр лежит на устройстве
 * (`holdScanFrame`), в адрес окна идёт только его номер, прочитанное название и штрихкод. Право `platform:catalog:write`
 * решает, видны ли «Создать» и «Прикрепить»; сервер проверит его сам.
 *
 * Каждое действие над ответом с номером поиска (`searchId`) — метка для обучения поиска (RP-FB77): «Это он», «Нет, другой», «Ничего из этого»,
 * «Создать новый» отмечаются сразу, «Найти товар и прикрепить фото» — окном прикрепления после сохранения (номер едет в адресе окна).
 */
export function useScanFlow(
  hints: SearchHints | null,
  searchId: string | null,
): { verdict: VerdictActions; session: ScanSessionVm; openProduct: (productId: string) => void } {
  const dispatch = useAppDispatch()
  const shell = useWindowShell()
  const { open } = useWindowStack()
  const access = useMyAccess()
  const [confirmWords] = platformSearchConfirm.useMutation()
  const [sendFeedback] = platformSearchFeedback.useMutation()
  const sessionState = useAppSelector((state) => state.platform.scanSession)
  const canWrite = grantsOf(access).permissions.has(CATALOG_WRITE)
  const session = useMemo(() => scanSessionVm(sessionState), [sessionState])

  // Товар, найденный сканером или поиском по фото, открывается ОБЩЕЙ карточкой — тем же правилом, что в дублях (`useOpenCommonProduct`).
  const openProduct = useOpenCommonProduct()

  const verdict = useMemo<VerdictActions>(() => {
    const record = (kind: ScanEventKind, name: string | null, productId: string | null) =>
      dispatch(scanEventRecorded({ kind, text: scanEventText(kind, name), productId, at: Date.now() }))
    /** Последний отправленный кадр уезжает в хранилище кадров; нет кадра — номер пустой, окна скажут, что снимок потерян. */
    const frame = (): string => {
      const photo = lastRetryPhoto()
      return photo === null ? '' : holdScanFrame(photo, photo.name)
    }
    const name = (hints?.suggestedName ?? '').slice(0, NAME_LIMIT)
    /** Метка для обучения поиска; нет номера — сервер поиск не записал, отмечать нечего. Сбой делу не мешает, но не молчит: строка в ленте смены. */
    const label = (action: SearchFeedbackAction, productId?: string) => {
      if (searchId === null) return
      void sendFeedback({ operationId: newUuid(), searchId, action, productId })
        .unwrap()
        .catch((error: unknown) => {
          const text = (error as { message?: string } | null)?.message ?? 'ответ сервера не получен'
          dispatch(scanEventRecorded({ kind: 'failed', text: `Отметка для обучения поиска не сохранилась: ${text}`, productId: productId ?? null, at: Date.now() }))
        })
    }
    return {
      canWrite,
      openHit: (hit) => {
        record('found', hit.name, hit.productId)
        openProduct(hit.productId)
      },
      confirmHit: (hit) => {
        record('found', hit.name, hit.productId)
        label('confirm', hit.productId)
        // Без права менять каталог запоминать нечем: «Да, это он» просто открывает карточку.
        if (!canWrite) {
          openProduct(hit.productId)
          return
        }
        // Текст снимка запоминается за ЭТИМ товаром; сбой не мешает прикрепить фото, но не молчит: строка «Не сохранено» в ленте смены.
        const words = (hints?.words ?? []).map((word) => ({ text: word.text, band: word.band }))
        if (words.length > 0) {
          void confirmWords({ operationId: newUuid(), productId: hit.productId, words })
            .unwrap()
            .catch((error: unknown) => {
              const text = (error as { message?: string } | null)?.message ?? 'ответ сервера не получен'
              dispatch(scanEventRecorded({ kind: 'failed', text: `Текст снимка не запомнился: ${text}`, productId: hit.productId, at: Date.now() }))
            })
        }
        // Окно прикрепления само сохраняет кадр (режим «confirm»): человек видит ход загрузки, ошибку и «Повторить» теми же средствами.
        open(shell.refOf(PLATFORM_WINDOWS.photoAttach, { productId: hit.productId }), { frame: frame(), from: 'scan', confirm: '1' })
      },
      rejectOffered: (hits) => {
        for (const hit of hits) label('reject', hit.productId)
      },
      noneOfThese: () => label('none-of-these'),
      createNew: () => {
        label('create')
        open(shell.refOf(PLATFORM_WINDOWS.commonProductNew), {
          from: 'scan',
          frame: frame(),
          name,
          barcode: hints?.barcode ?? '',
        })
      },
      findAndAttach: () => {
        open(shell.refOf(PLATFORM_WINDOWS.productPick), { purpose: 'attach-photo', frame: frame(), from: 'scan', name, searchId: searchId ?? '' })
      },
      skipped: () => {
        record('skipped', null, null)
      },
    }
  }, [canWrite, confirmWords, dispatch, hints, open, openProduct, searchId, sendFeedback, shell])

  return { verdict, session, openProduct }
}
